import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, Clock, Inbox, ChevronRight, RefreshCw, Loader2 } from 'lucide-react';
import EmptyState from '../EmptyState';
import { tiempoRelativo } from '../utils/helpers';

const SEVERIDAD_CONF = {
  amber: { Icon: Clock,          bg: 'bg-amber-50', text: 'text-amber-600' },
  rose:  { Icon: AlertTriangle,  bg: 'bg-rose-50',  text: 'text-rose-600' },
  slate: { Icon: Inbox,          bg: 'bg-slate-100', text: 'text-slate-500' },
};

/**
 * Lista compacta de alertas inteligentes generadas por IA.
 * Cada alerta: icono por severidad + texto + (opcional) link al proyecto.
 */
const AlertasFeed = ({ alertas, generating, error = null, onRegenerar }) => {
  const tieneAlertas = Array.isArray(alertas) && alertas.length > 0;
  const esNullCache = alertas === null;

  return (
    <div className="bg-white rounded-xl border border-slate-200/70 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="text-xs font-bold tracking-[0.1em] uppercase text-slate-600">
          Alertas
        </h3>
        {(tieneAlertas || error) && (
          <button
            onClick={onRegenerar}
            disabled={generating}
            title="Regenerar alertas"
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating
              ? <Loader2 size={10} className="animate-spin" />
              : <RefreshCw size={10} strokeWidth={2} />
            }
            Regenerar
          </button>
        )}
      </div>

      {generating && !tieneAlertas && (
        <ul>
          {[1, 2, 3].map((i) => (
            <li key={i} className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-3 skeleton">
              <div className="w-7 h-7 rounded-md bg-slate-100 flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-slate-100 rounded" style={{ width: '70%' }} />
                <div className="h-2.5 bg-slate-100 rounded" style={{ width: '40%' }} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {tieneAlertas && (
        <ul>
          {alertas.map((a, i) => {
            const conf = SEVERIDAD_CONF[a.severidad] || SEVERIDAD_CONF.slate;
            const Icon = conf.Icon;
            const Wrapper = a.proyecto_slug ? Link : 'div';
            const wrapperProps = a.proyecto_slug
              ? { to: `/proyectos/${a.proyecto_slug}`, className: 'group flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors' }
              : { className: 'flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0' };
            return (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: i * 0.03 }}
              >
                <Wrapper {...wrapperProps}>
                  <div className={`w-7 h-7 rounded-md ${conf.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={13} className={conf.text} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-slate-700 leading-snug">
                      {a.texto}
                    </p>
                    {a.generado_en && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {tiempoRelativo(a.generado_en)}
                      </p>
                    )}
                  </div>
                  {a.proyecto_slug && (
                    <ChevronRight
                      size={14}
                      className="text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0"
                      strokeWidth={2}
                    />
                  )}
                </Wrapper>
              </motion.li>
            );
          })}
        </ul>
      )}

      {!generating && (esNullCache || (Array.isArray(alertas) && alertas.length === 0)) && !error && (
        <EmptyState
          icon={Inbox}
          title={esNullCache ? 'Sin alertas generadas' : 'Sin alertas ahora'}
          description={esNullCache
            ? 'Genera la primera lectura inteligente del estado de tus proyectos.'
            : 'Todo en orden. La próxima revisión es automática.'}
          variant="card"
          action={esNullCache ? (
            <button
              onClick={onRegenerar}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-md transition-colors"
            >
              <RefreshCw size={11} strokeWidth={2} />
              Generar
            </button>
          ) : null}
        />
      )}

      {!generating && error && (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-rose-600 mb-2">No pudimos generar alertas.</p>
          <button
            onClick={onRegenerar}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
          >
            <RefreshCw size={11} strokeWidth={2} />
            Reintentar
          </button>
        </div>
      )}
    </div>
  );
};

export default AlertasFeed;
